import React, { FC, ReactElement, useRef, useEffect, useState } from 'react';
import cns from 'classnames';
import { useSelector } from 'react-redux';
import './MiniLanding.sass';
import { getMiniLandingData, getEventKey, IMiniLandingBanners } from '../../../../stores/reducers/userData';
import { internalizationState, getUniqueWatchKey } from '../../../../stores/reducers/sections';
import localeFieldResolver from '../../../../helpers/locale-field-finder';

interface Props {
  className?: string | Array<string>;
  bgSettings?: {
    color?: string;
    isDark?: boolean;
  };
  sectionName?: string;
  sectionData?: any;
  sectionId: string;
}

const MiniLanding: FC<Props> = ({ className, bgSettings, sectionName, sectionId, sectionData }): ReactElement => {
  const { activeLangState } = useSelector(internalizationState);
  const playerContainer = useRef(null);
  const eventKey = useSelector(getEventKey);
  const uniqueWatchKey = useSelector(getUniqueWatchKey);
  const {backendURL, minilanding} = useSelector(getMiniLandingData);
  const [miniLandingBackground, setMiniLandingBackground] = useState<any>();

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent);

  useEffect(() => {
    if (playerContainer && playerContainer.current) {
      (playerContainer.current! as HTMLElement).innerHTML = '';
      const el = document.createElement('div');
      el.id = 'comdi-player';
      el.setAttribute('data-comdi-event', uniqueWatchKey || eventKey);
      el.setAttribute('lang', activeLangState);
      (playerContainer.current! as HTMLElement).appendChild(el);
      if (el) {
        (window as any).broadcastLoader.createPlayer(el);
      }
    }
  }, [activeLangState, eventKey, uniqueWatchKey]);

  useEffect(() => {
    if (minilanding?.backgroundDesktopImage) {
      setMiniLandingBackground({
        backgroundImage: `url(${backendURL + minilanding.backgroundDesktopImage})`,
      });

      if (isMobile && minilanding.backgroundMobileImage) {
        setMiniLandingBackground({
          backgroundImage: `url(${backendURL + minilanding.backgroundMobileImage})`,
        });
      }
    } else {
      setMiniLandingBackground({
        backgroundColor: `${minilanding?.backgroundFillColor ?? '#FFFFFF'}`,
      });
    }
  }, [minilanding, backendURL, isMobile]);

  const floatAlignment = (alignment: string): string => {
    switch (alignment) {
      case 'right':
        return 'float-right';
      case 'left':
        return 'float-left';
      case 'center':
        return 'float-center';
      default:
        return '';
    }
  };

  return (
    <>
      <section
        className={cns('inner-section', 'mini-landing', bgSettings && bgSettings.isDark && 'inner-section--dark-bg', className)}
        style={miniLandingBackground}
      >
        {
          (minilanding && minilanding.banners) && (
            <div className="mini-landing__banners">
              {
                minilanding.banners.length > 0 ?
                  minilanding.banners.map((banner: IMiniLandingBanners) => {
                    if (banner.link) {
                      return (
                        <a
                          href={banner.link}
                          className={cns('mini-landing__banner', floatAlignment(banner.alignment ?? 'none'))}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                              display: 'block',
                              textAlign: 'center',
                              maxWidth: '100%'
                            }}
                          >
                            <picture>
                              <source media="(max-width: 480px)" srcSet={backendURL + (banner.mobileImage ?? banner.desktopImage as string)} />
                              <img src={backendURL + (banner.desktopImage as string ?? '')} alt="" />
                            </picture>
                        </a>
                      )
                    } else {
                      return (
                        <div
                          className={cns('mini-landing__banner', floatAlignment(banner.alignment ?? 'none'))}
                          style={{
                              textAlign: 'center',
                              maxWidth: '100%'
                            }}
                          >
                            <picture>
                              <source media="(max-width: 480px)" srcSet={backendURL + (banner.mobileImage ?? banner.desktopImage as string)} />
                              <img src={backendURL + (banner.desktopImage as string ?? '')} alt="" />
                            </picture>
                        </div>
                      )
                    }
                  })
                :
                  <></>
              }
              <div style={{clear: 'both'}}></div>
            </div>
          )
        }
        <div className="container" id={sectionId}>
          {
            minilanding?.text &&
            (
              <div className="mini-landing__text">
                <p
                  style={{
                    fontSize: minilanding.textSize + 'px' ?? '',
                    color: minilanding.textColor || '#000'
                  }}
                >
                  {localeFieldResolver(minilanding, 'text', activeLangState)}
                </p>
              </div>
            )
          }
          <div ref={playerContainer} />
        </div>
      </section>
    </>
  );
};

export default MiniLanding;
